<script lang="ts" setup>
import { i18n } from '@/i18n';
import { businessContentName } from '@/appLogic/business/businessTableHeaders';
import TableCell from '@/components/atom/TableCell.vue';
import TableHead from '@/components/atom/TableHead.vue';
import { nullOrUndefinedToMinusOne } from '@/coreDomainsModule/extends/number';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { TableType } from '@/coreDomainsModule/table';
import { TableCommonViewModel, TableContentViewModel } from '@/viewModels/tableContent';
import SelectTableCell from './SelectTableCell.vue';
import { inject, onMounted, watch } from 'vue';
import { OrganizationBusinessRepository } from '@/businessRepository/organizationBusinessRepository';
import { useRoute } from 'vue-router';
import { useSelectedBusinessStore } from '../pages/selectedBusinessStore';
import { storeToRefs } from 'pinia';
import { TableContentFontSize } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { TableHeaderFontSize } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import SelectInput from '@/corePagesModule/SelectInput';
import { key, useRecommendOrderStore } from '../pages/useRecommendOrderStore';

const route = useRoute();
const organizationBusinessRepository = new OrganizationBusinessRepository();

// props
const props = withDefaults(
  defineProps<{
    tableType: TableType;
    business: IBusiness;
    scrollEnable?: boolean;
    editableLeadTime?: boolean;
    editableStdInventory?: boolean;
    tableHeadFontSize?: TableContentFontSize;
    tableBodyFontSize?: TableHeaderFontSize;
  }>(),
  {
    scrollEnable: false,
    editableLeadTime: false,
    editableStdInventory: false,
    tableHeadFontSize: 'fontSizeS',
    tableBodyFontSize: 'fontSizeSS',
  }
);

// emits
const emits = defineEmits<{
  (e: 'onClickLeadTimeButton', event: MouseEvent): void;
  (e: 'onClickStdInventoryButton', event: MouseEvent): void;
}>();

//変数
const t = i18n.t;
const commonTableContentVm = new TableCommonViewModel();
const vm = new TableContentViewModel();

const selectedBusinessStore = useSelectedBusinessStore();
const { fetchState, updateSelectedBusinessId } = selectedBusinessStore;
const { state } = storeToRefs(selectedBusinessStore);
const recommendOrderStore = inject(key);
if (!recommendOrderStore) {
  throw new Error('store is undefined');
}

// イベントフック
onMounted(async () => {
  await fetchState(organizationBusinessRepository, props.business);
});

watch(
  () => props.business,
  async (newValue) => {
    await fetchState(organizationBusinessRepository, newValue);
  },
  {
    deep: true,
  }
);

watch(
  state.value.selectValue,
  (newValue) => {
    // マッチするビジネスを取得
    updateSelectedBusinessId(newValue);
  },
  {
    deep: true,
  }
);
</script>

<template>
  <div class="simple-business-table">
    <table class="table-container">
      <thead>
        <tr>
          <template v-for="(property, index) in commonTableContentVm.displayHeaders[tableType]" :key="index">
            <template
              v-if="
                (commonTableContentVm.mvpSimpleTableDisplayContents(property) && property !== 'MOQ' && property !== 'inventory') ||
                commonTableContentVm.commonTableDisplayContents(property) ||
                property === 'stdInventoryIndex'
              "
            >
              <TableHead :font-size="props.tableHeadFontSize" :label="businessContentName[property]" :text-align="'left'" scope="col" />
            </template>
          </template>
        </tr>
      </thead>
      <tbody class="body-container">
        <tr v-if="business" class="item-container">
          <template v-for="(property, propertyIndex) in commonTableContentVm.displayHeaders[tableType]" :key="propertyIndex">
            <!-- <template
              v-if="
                commonTableContentVm.mvpSimpleTableDisplayContents(property) || commonTableContentVm.commonTableDisplayContents(property)
              "
            > -->
            <!-- <template v-if="property === 'inventory'"> -->
            <!-- 現在庫数　 or 容量 -->
            <!-- <TableCell :tool-tip-value="vm.getDisplayValue(business[property], property)" :row-value="vm.getDisplayValue(business[property], property)" :text-align="'center'">
                  {{ vm.getDisplayValue(business[property], property) }}
                </TableCell>
              </template>
              <template v-else-if="property === 'leadTime'"> -->
            <!-- リードタイム -->
            <!-- <TableCell :tool-tip-value="vm.getLeadTime(nullOrUndefinedToMinusOne(business[property]))" class="editable-cell" :row-value="vm.getDisplayValue(business[property], property)" :text-align="'center'">
                  <div class="editable-cell__edit-content edit-content">
                    {{ vm.getLeadTime(nullOrUndefinedToMinusOne(business[property])) }}
                  </div>
                </TableCell> -->
            <!-- </template> -->
            <template v-if="property === 'warehouse'">
              <!-- 置き場名 -->
              <TableCell :font-size="props.tableBodyFontSize" :text-align="'left'">
                <SelectInput
                  :model-value="recommendOrderStore.state.selectedInfo.warehouse ?? ''"
                  :index="1"
                  :placeholder="t('置場名')"
                  :prop-aggregate-business-filter-property="recommendOrderStore.state.selectedInfo"
                  :label="''"
                  :filter-type="'WAREHOUSE'"
                  :is-open-prop="recommendOrderStore.state.isOpenFilterType === 'WAREHOUSE'"
                  use-teleport
                  @click:select-input="() => (recommendOrderStore.state.isOpenFilterType = 'WAREHOUSE')"
                  @update:model-value="recommendOrderStore.updateSelectedInfo($event, undefined, undefined, undefined, undefined)"
                />
              </TableCell>
            </template>
            <template v-else-if="property === 'supplierName'">
              <!-- 仕入先名 -->
              <TableCell :font-size="props.tableBodyFontSize" :text-align="'left'">
                <SelectInput
                  :model-value="recommendOrderStore.state.selectedInfo.supplierName ?? ''"
                  :index="2"
                  :placeholder="t('仕入先')"
                  :prop-aggregate-business-filter-property="recommendOrderStore.state.selectedInfo"
                  :label="''"
                  :filter-type="'SUPPLIER_NAME'"
                  :is-open-prop="recommendOrderStore.state.isOpenFilterType === 'SUPPLIER_NAME'"
                  use-teleport
                  @click:select-input="() => (recommendOrderStore.state.isOpenFilterType = 'SUPPLIER_NAME')"
                  @update:model-value="recommendOrderStore.updateSelectedInfo(undefined, $event, undefined, undefined, undefined)"
                />
              </TableCell>
            </template>
            <template v-else-if="property === 'businessNumber'">
              <!-- 商品名 -->
              <TableCell :font-size="props.tableBodyFontSize" :text-align="'left'">
                <SelectInput
                  :model-value="recommendOrderStore.state.selectedInfo.businessCode?.toString() ?? ''"
                  :index="3"
                  :placeholder="t('商品CD')"
                  :prop-aggregate-business-filter-property="recommendOrderStore.state.selectedInfo"
                  :label="''"
                  :filter-type="'BUSINESS_CODE'"
                  :is-open-prop="recommendOrderStore.state.isOpenFilterType === 'BUSINESS_CODE'"
                  use-teleport
                  @click:select-input="() => (recommendOrderStore.state.isOpenFilterType = 'BUSINESS_CODE')"
                  @update:model-value="recommendOrderStore.updateSelectedInfo(undefined, undefined, $event, undefined, undefined)"
                />
              </TableCell>
            </template>
            <template v-else-if="property === 'businessName'">
              <!-- 商品名 -->
              <TableCell :font-size="props.tableBodyFontSize" :text-align="'left'">
                <SelectInput
                  :model-value="recommendOrderStore.state.selectedInfo.businessName ?? ''"
                  :index="4"
                  :placeholder="t('商品')"
                  :prop-aggregate-business-filter-property="recommendOrderStore.state.selectedInfo"
                  :label="''"
                  :filter-type="'BUSINESS_NAME'"
                  :is-open-prop="recommendOrderStore.state.isOpenFilterType === 'BUSINESS_NAME'"
                  use-teleport
                  @click:select-input="() => (recommendOrderStore.state.isOpenFilterType = 'BUSINESS_NAME')"
                  @update:model-value="recommendOrderStore.updateSelectedInfo(undefined, undefined, undefined, $event, undefined)"
                />
              </TableCell>
            </template>
            <template v-else-if="property === 'package'">
              <!-- 現在庫数　 or 容量 -->
              <TableCell
                :font-size="props.tableBodyFontSize"
                :row-value="vm.getDisplayValue(business[property], property)"
                :text-align="'center'"
              >
                <SelectInput
                  :model-value="recommendOrderStore.state.selectedInfo.packageAmount?.toString() ?? ''"
                  :index="5"
                  :placeholder="t('容量')"
                  :prop-aggregate-business-filter-property="recommendOrderStore.state.selectedInfo"
                  :label="''"
                  :filter-type="'PACKAGE_AMOUNT'"
                  :is-open-prop="recommendOrderStore.state.isOpenFilterType === 'PACKAGE_AMOUNT'"
                  use-teleport
                  @click:select-input="() => (recommendOrderStore.state.isOpenFilterType = 'PACKAGE_AMOUNT')"
                  @update:model-value="recommendOrderStore.updateSelectedInfo(undefined, undefined, undefined, undefined, $event)"
                />
              </TableCell>
            </template>
            <!-- <template v-else> -->
            <!-- その他 -->
            <!-- <TableCell :tool-tip-value="vm.getDisplayValue(business[property], property)" :text-align="'left'">
                  {{ vm.getDisplayValue(business[property], property) }}
                </TableCell>
              </template> -->
            <!-- </template> -->
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.simple-business-table {
  overflow: v-bind('props.scrollEnable ? `scroll` : `hidden`');
  border-width: 1px;
  border-color: $borderDefault;
  border-radius: 4px;
}
.table-container {
  width: 100%;
  border-collapse: collapse;
  background-color: $semanticWhite;
  font-size: 14px; // TODO: 一旦tailwindのtext-smに合わせてるが、他のテーブルとも帳尻合わせつつちょうどいい値に直す
  line-height: 20px;
  border-radius: 4px;
}
.body-container {
  height: 64px;
}
.item-container {
  &__std-inventory-index {
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.edit-content {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  &__button {
    width: fit-content;
    margin: auto;
  }
}
</style>
